/**
 * Block component registry
 *
 * Maps block IDs to their React components and optional preview functions.
 * All components receive standardized BlockProps.
 *
 * @typedef {Object} BlockProps
 * @property {Object} data - Block-specific data from API
 * @property {Function} onRefresh - Callback to refresh block data
 * @property {string} clientId - Client identifier
 * @property {string} authSessionId - Auth session ID (optional)
 * @property {string} memberId - Member ID for localStorage caching (optional)
 */

import { lazy } from 'react';
import { quickPollPreview, announcementsPreview, smartBlockPreview } from './blockPreviews';
import { quickPollRequestParams } from './blockRequestParams';

const QuickPollBlock = lazy(() => import('./QuickPollBlock'));
const AnnouncementsBlock = lazy(() => import('./AnnouncementsBlock'));
const SmartBlock = lazy(() => import('./SmartBlock'));

/**
 * Block component registry
 */
export const BLOCKS = {
  'quick-poll': QuickPollBlock,
  announcements: AnnouncementsBlock,
  'smart-block': SmartBlock,
  // Future blocks:
  // 'data-quality': DataQualityBlock,
  // 'recommendations': RecommendationsBlock,
};

/**
 * Collapsed preview function registry
 * Each function: (data) => string | null
 */
export const BLOCK_PREVIEWS = {
  'quick-poll': quickPollPreview,
  announcements: announcementsPreview,
  'smart-block': smartBlockPreview,
};

/**
 * Per-block request-param augmentation registry.
 * Each function: ({ clientId, memberId }) => Record<string, string> | null
 */
export const BLOCK_REQUEST_PARAMS = {
  'quick-poll': quickPollRequestParams,
};

/**
 * Get collapsed preview text for a block
 * @param {string} blockId - Block identifier
 * @param {Object} data - Block data from API
 * @returns {string|null} Preview text or null
 */
export function getBlockPreview(blockId, data) {
  const previewFn = BLOCK_PREVIEWS[blockId];
  return previewFn ? previewFn(data) : null;
}

/**
 * Get extra query params a block needs to send on its fetch.
 * @param {string} blockId - Block identifier
 * @param {{ clientId: string, memberId?: string }} ctx
 * @returns {Record<string, string> | null}
 */
export function getBlockRequestParams(blockId, ctx) {
  const fn = BLOCK_REQUEST_PARAMS[blockId];
  return fn ? fn(ctx) : null;
}
